import { prependChatMessageToInfinitePaginatedData } from '@client-info/features/chat/helpers/prependChatMessageToInfinitePaginatedData';
import { errorMessageAtom } from '@client-info/features/ErrorSnackbar';
import { orpc } from '@client-info/utils/orpc';
import { infinitePaginationOptions } from '@common/infinitePagination';
import type { ChatTimelineEntry } from '@server/chat/chat.dto';
import type { PaginatedResult } from '@server/common/paginated.dto';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

export const queryKey = ['chat', 'byAccessCode'];
export const messagesQueryKey = ['chat', 'messagesByAccessCode'];

export const getMessagesQueryKey = (accessCode: string) => [
  ...messagesQueryKey,
  accessCode,
];

const getChatQueryKey = (accessCode: string) => [...queryKey, accessCode];

export const useGetChatByAccessCode = (accessCode: string) =>
  useQuery({
    ...orpc.chat.byAccessCode.queryOptions({
      queryKey: getChatQueryKey(accessCode),
      input: { accessCode },
    }),
  });

export const useGetMessagesByAccessCodeInfinite = (accessCode: string) =>
  useInfiniteQuery(
    orpc.chat.getMessagesByAccessCode.infiniteOptions({
      ...infinitePaginationOptions,
      queryKey: getMessagesQueryKey(accessCode),
      input: (pageParam) => ({
        ...infinitePaginationOptions.input(pageParam),
        accessCode,
      }),
    }),
  );

export const useSendMessageByAccessCode = (accessCode: string) => {
  const queryClient = useQueryClient();
  const setErrorMessage = useSetAtom(errorMessageAtom);

  return useMutation(
    orpc.chat.sendMessageByAccessCode.mutationOptions({
      onSuccess: (message) => {
        queryClient.setQueryData<
          InfiniteData<PaginatedResult<ChatTimelineEntry>, number>
        >(getMessagesQueryKey(accessCode), (prevState) =>
          prependChatMessageToInfinitePaginatedData(prevState, message),
        );
      },
      onError: () => {
        setErrorMessage('Failed to send chat message');
      },
    }),
  );
};

export const useLeaveChatByAccessCode = (accessCode: string) => {
  const queryClient = useQueryClient();
  const setErrorMessage = useSetAtom(errorMessageAtom);

  return useMutation(
    orpc.chat.leaveByAccessCode.mutationOptions({
      onSuccess: (chat) => {
        queryClient.setQueryData(getChatQueryKey(accessCode), chat);
        queryClient.invalidateQueries({
          queryKey: getMessagesQueryKey(accessCode),
        });
      },
      onError: () => {
        setErrorMessage('Failed to leave chat');
      },
    }),
  );
};
